import type { SingleBudget } from '@/store/expenses-store/budget/budget.dto';
import type {
  TransactionCategory,
  TransactionRow,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import { amountInputError, parseAmount } from '@/utils/form';
import { formatCurrency } from '@/utils/formatCurrency.ts';
import { isInflow } from '@pages/Expenses/TransactionsPage/utils/transactionColors';

import type { BudgetStatus, Category } from './BudgetDetail.constants';

/** The plan of a category for the year, keyed the way budgets reference it: by category id. */
export type CategoryPlan = { id: string; planned: number };

export function plansByCategoryId(budgets: SingleBudget[] | undefined): Map<string, CategoryPlan> {
  const map = new Map<string, CategoryPlan>();
  for (const budget of budgets ?? []) {
    map.set(budget.expensesCategory, {
      id: budget.id,
      planned: Number(budget.plannedBudget.number) || 0,
    });
  }
  return map;
}

/**
 * Joins the vessel's categories to their plan and their spend. Everything is keyed by
 * category id: slugs move when a category is renamed, so they cannot carry the join.
 */
export function toBudgetCategories(
  categories: TransactionCategory[],
  rows: TransactionRow[],
  plans: Map<string, CategoryPlan>,
): Category[] {
  const rowsByCategory = new Map<string, TransactionRow[]>();
  for (const row of rows) {
    if (!row.categoryId) continue;
    const bucket = rowsByCategory.get(row.categoryId);
    if (bucket) bucket.push(row);
    else rowsByCategory.set(row.categoryId, [row]);
  }

  return categories.map((cat) => ({
    id: cat.id,
    name: cat.title,
    icon: cat.icon,
    color: cat.color,
    isDefault: cat.is_default,
    hasPlan: plans.has(cat.id),
    planned: plans.get(cat.id)?.planned ?? 0,
    spent: sumSpent(rowsByCategory.get(cat.id) ?? []),
  }));
}

export function sumSpent(rows: TransactionRow[]): number {
  // Rounded back to cents so float drift from summing decimal amounts never leaks into totals.
  return parseAmount(
    rows.reduce(
      (total, row) => total + (isInflow(row.transactionType) ? -row.amount : row.amount),
      0,
    ),
  );
}

export function getStatus(spent: number, planned: number): BudgetStatus {
  if (planned <= 0) return spent > 0 ? 'over' : 'ok';
  const pct = spent / planned;
  if (pct >= 1) return 'over';
  if (pct >= 0.8) return 'warn';
  return 'ok';
}

export function fmt(n: number): string {
  return '$' + formatCurrency(n);
}

export function spentPct(spent: number, planned: number): number {
  if (planned <= 0) return spent > 0 ? 100 : 0;
  return Math.round((spent / planned) * 100);
}

export function planInputError(
  raw: string,
  message = 'Please enter an amount with up to 2 decimal places.',
): string | undefined {
  return amountInputError(raw, message);
}

/** Planned amount as cents-rounded number; '' is the caller's signal to drop the plan. */
export function parsePlannedAmount(raw: string): number {
  return parseAmount(raw.trim());
}
